import { Box, Button, Heading, Input } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { EmptyState } from '../../../components/EmptyState'
import { FormField } from '../../../components/FormField'
import { LoadingBlock } from '../../../components/Loading'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import {
  IFullStep,
  IPersonnelMovementRequestsAnswer,
  IPersonnelMovementRequestsStorePayload
} from '../../../services/PersonnelMovementRequests/interfaces'
import PersonnelMovementRequestsService from '../../../services/PersonnelMovementRequests/PersonnelMovementRequestsService'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { IUser, UserService } from '../../../services/User/UserService'

/** Question ids of the first step, mapped to the API fields they fill. */
const QUESTION_FIELDS: Record<number, keyof IPersonnelMovementRequestsStorePayload> = {
  1: 'name',
  2: 'email',
  3: 'sector_id',
  4: 'type'
}

const EMAIL_QUESTION_ID = 2

interface IPersonnelMovementBaseFormProps {
  /** Values already saved, when the requester comes back to the first step. */
  initial?: IPersonnelMovementRequestsStorePayload
  submitLabel: ReactNode
  /** Saves the step. A rejected promise with the API's validation errors is shown on the fields. */
  onSubmit: (payload: IPersonnelMovementRequestsStorePayload) => Promise<void>
}

/**
 * First step of the MP: requester (from the managers list, which fills the e-mail), e-mail,
 * sector and movement type. Used to open an MP and to rewrite these answers after going back.
 */
export function PersonnelMovementBaseForm({ initial, submitLabel, onSubmit }: IPersonnelMovementBaseFormProps) {
  const toast = useAppToast()
  const service = useMemo(() => new PersonnelMovementRequestsService(), [])
  const [fullStep, setFullStep] = useState<IFullStep | null>(null)
  const [stepFailed, setStepFailed] = useState(false)
  const [sectors, setSectors] = useState<ISector[]>([])
  const [managers, setManagers] = useState<IUser[]>([])
  const [answers, setAnswers] = useState<IPersonnelMovementRequestsAnswer>((): IPersonnelMovementRequestsAnswer => {
    if (!initial) return {}
    return { 1: initial.name, 2: initial.email, 3: String(initial.sector_id), 4: initial.type }
  })
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [saving, setSaving] = useState(false)

  const loadStep = () => {
    setStepFailed(false)
    service
      .getStep(1)
      .then((response) => setFullStep(response.data))
      .catch((error) => {
        setStepFailed(true)
        toast.error('Erro ao carregar as perguntas da solicitação', error)
      })
  }

  useEffect(() => {
    loadStep()
    new SectorService()
      .listForSelect()
      .then(setSectors)
      .catch((error) => toast.error('Erro ao carregar os setores', error))
    new UserService()
      .getManagers()
      .then(setManagers)
      .catch((error) => toast.error('Erro ao carregar os gestores', error))
  }, [])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      await onSubmit({
        name: answers[1] ?? '',
        email: answers[2] ?? '',
        sector_id: Number(answers[3]),
        type: answers[4] ?? ''
      })
    } catch (error) {
      const apiErrors = (error as AxiosError<{ errors?: Record<string, string[]> }>).response?.data?.errors
      if (apiErrors && Object.keys(apiErrors).length > 0) {
        setErrors(apiErrors)
        // Errors of no field on this step go in the description, since no field shows them.
        const fields: string[] = Object.values(QUESTION_FIELDS)
        const unmapped = Object.entries(apiErrors).filter(([key]) => !fields.includes(key))
        toast.invalidForm(unmapped.flatMap(([, messages]) => messages).join(' ') || undefined)
        return
      }
      toast.error('Erro ao salvar a solicitação', error)
    } finally {
      setSaving(false)
    }
  }

  if (stepFailed) {
    return (
      <EmptyState
        tone="error"
        icon="lni lni-warning"
        title="Não foi possível carregar a solicitação"
        action={<ActionButton action="refresh" label="Tentar novamente" showLabel onClick={loadStep} />}
      />
    )
  }

  if (!fullStep) return <LoadingBlock />

  return (
    <>
      <Heading as="h2" mb="4">
        {fullStep.step.title}
      </Heading>

      <form onSubmit={handleSubmit}>
        {fullStep.questions.map((question) => (
          <FormField
            key={question.id}
            isRequired
            mb="4"
            label={question.label}
            error={errors[QUESTION_FIELDS[question.id]]}
          >
            {question.type === 'text' && (
              <Input
                value={answers[question.id] || ''}
                onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })}
              />
            )}
            {question.type === 'select' && (
              <FormSelect
                placeholder="Selecione uma opção"
                value={answers[question.id] || ''}
                onChange={(value) => {
                  const next = { ...answers, [question.id]: value }
                  if (question.options === 'managers') {
                    next[EMAIL_QUESTION_ID] = managers.find((manager) => manager.name === value)?.email || ''
                  }
                  setAnswers(next)
                }}
                options={
                  question.options
                    ? question.options === 'managers'
                      ? managers.map((manager) => ({ value: manager.name, label: manager.name }))
                      : question.options.map((option) => ({ value: option, label: option }))
                    : sectors.map((sector) => ({ value: sector.id, label: sector.name }))
                }
              />
            )}
          </FormField>
        ))}
        <Box>
          <Button colorScheme="magenta" type="submit" isLoading={saving} w={{ base: '100%', md: 'auto' }}>
            {submitLabel}
          </Button>
        </Box>
      </form>
    </>
  )
}
